import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Apple, CheckCircle2, Loader2, Play, ShieldCheck } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/cn'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import {
  useAssociateTotp,
  useDisableTotp,
  useMfaEnrollStart,
  useMfaEnrollVerify,
  useMfaStatus,
  useReauthCognito,
  useVerifyTotp,
} from './queries'
import type { AuthProvider } from './types'
import { AUTHENTICATOR_STORES } from './stores'

type Step = 'idle' | 'reauth' | 'scan' | 'verify' | 'done' | 'disable'

const PROVIDER_LABEL: Record<AuthProvider, string> = {
  cognito: 'Cognito',
  google: 'Google',
  microsoft: 'Microsoft',
}

export function TwoFactorAuthCard({ isFirstTime = false }: { isFirstTime?: boolean } = {}) {
  const { user } = useAuth()
  const navigate = useNavigate()

  const status = useMfaStatus()
  const enabled = status.data?.enabled ?? false
  const provider: AuthProvider = (status.data?.authProvider as AuthProvider) ?? 'cognito'
  const isSocial = provider !== 'cognito'

  const [step, setStep] = useState<Step>('idle')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null)
  const [qrDataUri, setQrDataUri] = useState<string | null>(null)
  const [secretCode, setSecretCode] = useState<string>('')

  const [reauthPassword, setReauthPassword] = useState('')
  const [verifyCode, setVerifyCode] = useState('')
  const [disablePassword, setDisablePassword] = useState('')
  const [disableCode, setDisableCode] = useState('')

  useEffect(() => {
    if (status.isSuccess && enabled) setStep('done')
  }, [status.isSuccess, enabled])

  const reauthM = useReauthCognito()
  const associateM = useAssociateTotp()
  const enrollStartM = useMfaEnrollStart()
  const verifyM = useVerifyTotp()
  const enrollVerifyM = useMfaEnrollVerify()
  const disableM = useDisableTotp()

  const anyBusy =
    reauthM.isPending ||
    associateM.isPending ||
    enrollStartM.isPending ||
    verifyM.isPending ||
    enrollVerifyM.isPending ||
    disableM.isPending

  const clearMsgs = () => {
    setError(null)
    setSuccess(null)
  }

  const resetToIdle = () => {
    clearMsgs()
    setAccessToken(null)
    setEnrollmentToken(null)
    setQrDataUri(null)
    setSecretCode('')
    setReauthPassword('')
    setVerifyCode('')
    setStep('idle')
  }

  const handleEnableClick = () => {
    clearMsgs()
    setStep('reauth')
  }

  const handleReauthSubmit = async () => {
    clearMsgs()
    if (!user?.email) {
      setError('Not signed in.')
      return
    }
    try {
      if (isSocial) {
        const res = await enrollStartM.mutateAsync()
        setEnrollmentToken(res.enrollmentToken)
        setQrDataUri(res.qrDataUri ?? null)
        setSecretCode(res.secretCode)
        setStep('scan')
        return
      }
      if (!reauthPassword) {
        setError('Password is required.')
        return
      }
      const signin = await reauthM.mutateAsync({ email: user.email, password: reauthPassword })
      if (signin.step !== 'done' || !signin.accessToken) {
        throw new Error(
          'Account already has two-factor authentication enabled, or requires additional setup.',
        )
      }
      setAccessToken(signin.accessToken)
      const assoc = await associateM.mutateAsync({
        accessToken: signin.accessToken,
        email: user.email,
      })
      setQrDataUri(assoc.qrDataUri ?? null)
      setSecretCode(assoc.secretCode)
      setReauthPassword('')
      setStep('scan')
    } catch (err) {
      setError(messageFor(err, 'Failed to start enrollment.'))
    }
  }

  const handleVerifySubmit = async () => {
    clearMsgs()
    const code = verifyCode.trim()
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code.')
      return
    }
    try {
      if (isSocial) {
        if (!enrollmentToken) throw new Error('Missing enrollment token.')
        await enrollVerifyM.mutateAsync({ enrollmentToken, userCode: code })
      } else {
        if (!accessToken || !user?.email) throw new Error('Missing access token.')
        await verifyM.mutateAsync({ accessToken, userCode: code, email: user.email })
      }
      setSuccess('Two-factor authentication enabled.')
      setAccessToken(null)
      setEnrollmentToken(null)
      setStep('done')
      if (isFirstTime) {
        setTimeout(() => navigate('/dashboard', { replace: true }), 1200)
      }
    } catch (err) {
      setError(messageFor(err, 'Verification failed.'))
    }
  }

  const handleDisableClick = () => {
    clearMsgs()
    setDisablePassword('')
    setDisableCode('')
    setStep('disable')
  }

  const handleDisableSubmit = async () => {
    clearMsgs()
    const code = disableCode.trim()
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your authenticator app.')
      return
    }
    if (!isSocial && !disablePassword) {
      setError('Password is required.')
      return
    }
    try {
      await disableM.mutateAsync({
        userCode: code,
        password: isSocial ? undefined : disablePassword,
      })
      setSuccess('Two-factor authentication disabled.')
      setStep('idle')
    } catch (err) {
      setError(messageFor(err, 'Failed to disable.'))
    }
  }

  if (!user) return null
  if (status.isLoading) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto size-5 animate-spin" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <ShieldCheck className="size-5" />
          <span>Security · Two-Factor Authentication</span>
          <Badge variant={enabled ? 'success' : 'warning'}>
            {enabled ? 'Enabled' : 'Not enabled'}
          </Badge>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Add an authenticator app like Google Authenticator, Authy, or 1Password to protect your account.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {success ? (
          <Alert variant="success">
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        ) : null}

        {step === 'idle' ? <IdleStep onEnable={handleEnableClick} /> : null}

        {step === 'reauth' ? (
          <ReauthStep
            isSocial={isSocial}
            provider={provider}
            password={reauthPassword}
            onPasswordChange={setReauthPassword}
            onSubmit={handleReauthSubmit}
            onCancel={resetToIdle}
            busy={reauthM.isPending || associateM.isPending || enrollStartM.isPending}
            disabled={anyBusy}
          />
        ) : null}

        {step === 'scan' ? (
          <ScanStep
            qrDataUri={qrDataUri}
            secretCode={secretCode}
            onNext={() => {
              clearMsgs()
              setStep('verify')
            }}
            onCancel={resetToIdle}
          />
        ) : null}

        {step === 'verify' ? (
          <VerifyStep
            code={verifyCode}
            onChange={setVerifyCode}
            onSubmit={handleVerifySubmit}
            onCancel={resetToIdle}
            busy={verifyM.isPending || enrollVerifyM.isPending}
          />
        ) : null}

        {step === 'done' ? <DoneStep onDisable={handleDisableClick} /> : null}

        {step === 'disable' ? (
          <DisableStep
            isSocial={isSocial}
            password={disablePassword}
            onPasswordChange={setDisablePassword}
            code={disableCode}
            onCodeChange={setDisableCode}
            onSubmit={handleDisableSubmit}
            onCancel={() => setStep('done')}
            busy={disableM.isPending}
          />
        ) : null}
      </CardContent>
    </Card>
  )
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.body && typeof err.body === 'object' && 'error' in err.body) {
      const m = (err.body as { error: unknown }).error
      if (typeof m === 'string' && m) return m
    }
    if (err.message) return err.message
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function IdleStep({ onEnable }: { onEnable: () => void }) {
  return (
    <div className="space-y-4">
      <StoreBadges title="Don't have an authenticator app? Install Google Authenticator:" />
      <Button onClick={onEnable}>
        <ShieldCheck className="size-4" /> Enable Two-Factor Authentication
      </Button>
    </div>
  )
}

function ReauthStep({
  isSocial,
  provider,
  password,
  onPasswordChange,
  onSubmit,
  onCancel,
  busy,
  disabled,
}: {
  isSocial: boolean
  provider: AuthProvider
  password: string
  onPasswordChange: (v: string) => void
  onSubmit: () => void
  onCancel: () => void
  busy: boolean
  disabled: boolean
}) {
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!busy) onSubmit()
      }}
    >
      <StepBadge current={1} />
      {isSocial ? (
        <p className="text-sm">
          You signed in with {PROVIDER_LABEL[provider]} - no password needed. Click Continue to see your QR code.
        </p>
      ) : (
        <>
          <p className="text-sm">For security, please confirm your password to continue.</p>
          <FormField
            label="Password"
            type="password"
            autoComplete="current-password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => onPasswordChange(e.currentTarget.value)}
            autoFocus
            disabled={disabled}
            required
          />
        </>
      )}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || disabled}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : null}
          {busy ? 'Please wait…' : 'Continue'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function ScanStep({
  qrDataUri,
  secretCode,
  onNext,
  onCancel,
}: {
  qrDataUri: string | null
  secretCode: string
  onNext: () => void
  onCancel: () => void
}) {
  return (
    <div className="space-y-4">
      <StepBadge current={2} />
      <p className="text-sm">Scan this QR code with your authenticator app.</p>
      <div className="rounded-md border bg-muted/40 p-4">
        <p className="mb-3 text-xs font-bold uppercase tracking-wider text-primary">How to do this</p>
        <ol className="space-y-2 text-sm">
          {HOW_TO_STEPS.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-pill bg-primary text-xs font-bold text-primary-foreground">
                {i + 1}
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>
      <StoreBadges title="Need an authenticator app? Install Google Authenticator:" />
      <div className="flex flex-col items-center gap-4 rounded-md border border-primary/20 bg-primary/5 p-5 sm:flex-row">
        {qrDataUri ? (
          <img
            src={qrDataUri}
            alt="TOTP QR code"
            className="size-48 rounded-sm bg-white p-2"
          />
        ) : null}
        <div className="flex-1 text-sm text-muted-foreground">
          Can&rsquo;t scan? Enter this secret manually:
          <code className="mt-2 block break-all rounded-sm border bg-card p-2 font-mono text-xs text-foreground">
            {secretCode}
          </code>
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={onNext}>I&rsquo;ve scanned it</Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function VerifyStep({
  code,
  onChange,
  onSubmit,
  onCancel,
  busy,
}: {
  code: string
  onChange: (v: string) => void
  onSubmit: () => void
  onCancel: () => void
  busy: boolean
}) {
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!busy) onSubmit()
      }}
    >
      <StepBadge current={3} />
      <p className="text-sm">Enter the 6-digit code from your authenticator app.</p>
      <FormField
        label="Verification code"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        placeholder="000000"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => onChange(e.currentTarget.value)}
        autoFocus
        className="text-center font-mono text-xl tracking-[10px]"
      />
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
          Verify & Enable
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function DoneStep({ onDisable }: { onDisable: () => void }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-success">
        Two-factor authentication is enabled on your account.
      </p>
      <p className="text-sm text-muted-foreground">
        Signing in from a new device will ask for a 6-digit code from your authenticator app in addition to your password.
      </p>
      <Button variant="danger" onClick={onDisable}>
        <AlertTriangle className="size-4" /> Disable Two-Factor Authentication
      </Button>
    </div>
  )
}

function DisableStep({
  isSocial,
  password,
  onPasswordChange,
  code,
  onCodeChange,
  onSubmit,
  onCancel,
  busy,
}: {
  isSocial: boolean
  password: string
  onPasswordChange: (v: string) => void
  code: string
  onCodeChange: (v: string) => void
  onSubmit: () => void
  onCancel: () => void
  busy: boolean
}) {
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!busy) onSubmit()
      }}
    >
      <p className="text-sm font-medium">
        To turn off two-factor authentication, confirm you still have access to your authenticator app.
      </p>
      {!isSocial ? (
        <FormField
          label="Password"
          type="password"
          autoComplete="current-password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => onPasswordChange(e.currentTarget.value)}
          disabled={busy}
          required
          autoFocus
        />
      ) : null}
      <FormField
        label="6-digit code from your authenticator"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        placeholder="000000"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => onCodeChange(e.currentTarget.value)}
        disabled={busy}
        autoFocus={isSocial}
        className="text-center font-mono text-xl tracking-[10px]"
      />
      <div className="flex gap-2">
        <Button type="submit" variant="danger" disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : null}
          {busy ? 'Please wait…' : 'Disable'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function StepBadge({ current }: { current: 1 | 2 | 3 }) {
  return (
    <span className="inline-flex items-center rounded-pill bg-primary/10 px-3 py-0.5 text-[11px] font-bold uppercase tracking-wider text-primary">
      Step {current} of 3
    </span>
  )
}

function StoreBadges({ title }: { title: string }) {
  return (
    <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground">
      <p className="mb-2 font-semibold text-foreground">{title}</p>
      <div className="flex flex-wrap gap-2">
        {AUTHENTICATOR_STORES.map((s) => (
          <a
            key={s.id}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex min-w-[150px] items-center gap-2 rounded-md px-4 py-2 shadow-card transition-transform hover:-translate-y-px',
              'bg-black text-white',
            )}
          >
            {s.id === 'apple' ? <Apple className="size-5" /> : <Play className="size-5" />}
            <span className="flex flex-col leading-tight">
              <span className="text-[9px] uppercase tracking-wider opacity-80">
                {s.smallLabel}
              </span>
              <span className="text-sm font-semibold">{s.label}</span>
            </span>
          </a>
        ))}
      </div>
    </div>
  )
}

const HOW_TO_STEPS = [
  <>
    <strong className="font-semibold">Open your authenticator app</strong> (Google Authenticator, Authy, 1Password, etc.) on your phone.
  </>,
  <>
    Tap the <strong className="font-semibold">+</strong> button and choose{' '}
    <strong className="font-semibold">Scan a QR code</strong>.
  </>,
  <>
    Point your phone camera at the QR code below - the app will add a new{' '}
    <strong className="font-semibold">Bokapsys</strong> entry with a 6-digit code that refreshes every 30 seconds.
  </>,
  <>
    Click <strong className="font-semibold">I&rsquo;ve scanned it</strong> below, then enter the current 6-digit code shown in your authenticator app.
  </>,
] as const
